import React, { useEffect, useMemo, useState } from 'react';

import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { Button, Select, Spinner, Table, Tabs } from '@bringg/react-components';
import { FleetType } from '@bringg/dashboard-sdk/dist/DeliveryCatalog/DeliveryCatalog.consts';
import { CarrierCancellationReason, Nullable } from '@bringg/types';

import { useStores } from '../../../../recipes';

import styles from './delivery-catalog-connect-cancellation-reasons.module.scss';

interface Props {
	fleetType: FleetType;
}

const ALL_TAB = 'all';
const UNMAPPED_TAB = 'unmapped';

const DeliveryCatalogConnectCancellationReasons = observer(({ fleetType }: Props) => {
	const { t } = useTranslation();
	const { carrierCancellationReasonMappingStore, usersStore } = useStores();
	const [activeSubTab, setActiveSubTab] = useState<string>(ALL_TAB);

	useEffect(() => {
		if (fleetType != null) {
			void carrierCancellationReasonMappingStore.fetchReasons(fleetType);
		}
	}, [fleetType, carrierCancellationReasonMappingStore]);

	const isAdmin = usersStore.isAdmin;
	const {
		carrierCancellationReasons,
		fdmReasons,
		stagedMappings: stagedCancellationReasonMappings,
		unmappedCount: unmappedCancellationReasonsCount,
		isDirty: isCancellationReasonMappingDirty,
		isFetching: isCancellationReasonsFetching,
		isSaving: isCancellationReasonsSaving
	} = carrierCancellationReasonMappingStore;

	const fdmOptions = useMemo(
		() =>
			fdmReasons
				// Loose `!= null` is deliberate: it also drops reasons whose `merchant_id` is absent
				// (hagmonia older than PR #12785 does not serialize it), so the picker fails closed
				// instead of offering global default reasons that the backend rejects.
				.filter(reason => reason.merchant_id != null)
				.map(reason => ({ id: reason.id, name: reason.reason ?? '' })),
		[fdmReasons]
	);

	const visibleReasons =
		activeSubTab === UNMAPPED_TAB
			? carrierCancellationReasons.filter(reason => stagedCancellationReasonMappings.get(reason.id) == null)
			: carrierCancellationReasons;

	const columns = [
		{
			title: t('DELIVERY_PROVIDER_CATALOG_CONNECT.CANCELLATION_REASONS.CARRIER_REASON'),
			dataIndex: 'reason',
			key: 'reason',
			width: '50%',
			ellipsis: true,
			render: (reason: Nullable<string>) => reason ?? '-'
		},
		{
			title: t('DELIVERY_PROVIDER_CATALOG_CONNECT.CANCELLATION_REASONS.FDM_REASON'),
			key: 'fdmReason',
			width: '50%',
			onCell: () => ({ className: styles.fdmCell }),
			render: (_: unknown, record: CarrierCancellationReason) => (
				<Select
					className={styles.fdmReasonSelect}
					bordered={false}
					placeholder={t('DELIVERY_PROVIDER_CATALOG_CONNECT.CANCELLATION_REASONS.SELECT_A_REASON')}
					// antd renders `null` as an empty selected item; pass `undefined` so a cleared row shows the placeholder
					value={stagedCancellationReasonMappings.get(record.id) ?? undefined}
					allowClear
					options={fdmOptions}
					disabled={!isAdmin || isCancellationReasonsSaving}
					onChange={(value: number | null) =>
						carrierCancellationReasonMappingStore.setMapping(record.id, value)
					}
				/>
			)
		}
	];

	const subTabItems = [
		{
			label: `${t('DELIVERY_PROVIDER_CATALOG_CONNECT.CANCELLATION_REASONS.ALL')} (${
				carrierCancellationReasons.length
			})`,
			key: ALL_TAB
		},
		{
			label: `${t(
				'DELIVERY_PROVIDER_CATALOG_CONNECT.CANCELLATION_REASONS.UNMAPPED'
			)} (${unmappedCancellationReasonsCount})`,
			key: UNMAPPED_TAB
		}
	];

	if (isCancellationReasonsFetching) {
		return <Spinner className="spinner-center" />;
	}

	return (
		<div className={styles.root}>
			<div className={styles.content}>
				<div className={styles.header}>
					<h1 className={styles.headerTitle}>
						{t('DELIVERY_PROVIDER_CATALOG_CONNECT.CANCELLATION_REASONS.TITLE')}
					</h1>
					<p className={styles.headerSubtitle}>
						{t('DELIVERY_PROVIDER_CATALOG_CONNECT.CANCELLATION_REASONS.SUBTITLE')}
					</p>
				</div>
				<div className={styles.subTabsWrapper}>
					<Tabs activeKey={activeSubTab} onChange={setActiveSubTab} items={subTabItems} />
				</div>
				<Table
					isTableWrapper={false}
					bordered
					rowKey="id"
					columns={columns}
					dataSource={visibleReasons}
					pagination={false}
				/>
			</div>
			{isAdmin && (
				<div className={styles.footer}>
					<Button
						type="inline-link"
						onClick={() => carrierCancellationReasonMappingStore.discardMappings()}
						disabled={!isCancellationReasonMappingDirty || isCancellationReasonsSaving}
					>
						{t('DELIVERY_PROVIDER_CATALOG_CONNECT.FOOTER.DISCARD_CHANGES')}
					</Button>
					<Button
						type="primary"
						loading={isCancellationReasonsSaving}
						disabled={!isCancellationReasonMappingDirty}
						onClick={() => carrierCancellationReasonMappingStore.saveMappings(fleetType)}
					>
						{t('DELIVERY_PROVIDER_CATALOG_CONNECT.CANCELLATION_REASONS.SAVE')}
					</Button>
				</div>
			)}
		</div>
	);
});

export default DeliveryCatalogConnectCancellationReasons;
